import rgb from 'color-space/rgb.js';
import xyz from 'color-space/xyz.js';
import lab from 'color-space/lab.js';

function _filled_array(fillWith, count) {
  const array = [];
  for (let i = 0; i < count; i++) {
    array.push(fillWith);
  }
  return array;
}
function euclidean_distance_squared_index(a, a_start, b) {
  const r = a[a_start++] - b[0];
  const g = a[a_start++] - b[1];
  const _b = a[a_start++] - b[2];
  return r ** 2 + g ** 2 + _b ** 2;
}
function kmeans(data, k, maxAttempt, thresold = 2, clipAlpha = false) {
  const cluster_sum = [];
  const thresold_squared = thresold ** 2;
  let pixelCount = data.length / 4;
  let cluster_centers = [];
  let new_cluster_centers = [];
  let iteration = 0;
  const isDataTypedArray = data instanceof Uint8ClampedArray;
  const PIXEL_LEN = clipAlpha ? 3 : 4;
  if (clipAlpha) {
    let j = 0;
    const dataNoAlpha = isDataTypedArray ? new Uint8ClampedArray(pixelCount * 3) : [];
    for (let i = 0; i < data.length; ) {
      if (data[i + 3] == 0) {
        i += 4;
        continue;
      }
      dataNoAlpha[j++] = data[i++];
      dataNoAlpha[j++] = data[i++];
      dataNoAlpha[j++] = data[i++];
      i++;
    }
    data = dataNoAlpha.slice(0, j);
    pixelCount = j / 3;
  }
  for (let i = 0; i < k; i++) {
    const start = Math.floor(1 * (pixelCount - 1)) * PIXEL_LEN;
    cluster_centers.push(
      /* Array.from */
      data.slice(start, start + 3)
    );
    new_cluster_centers.push(isDataTypedArray ? new Uint8ClampedArray(3) : _filled_array(0, 3));
    cluster_sum.push(_filled_array(0, 4));
  }
  while (iteration < maxAttempt) {
    for (let i = 0; i < data.length; ) {
      let cluster_index = 0;
      let _min_distance = euclidean_distance_squared_index(data, i, cluster_centers[0]);
      for (let j = 1; j < k; j++) {
        const distance = euclidean_distance_squared_index(data, i, cluster_centers[j]);
        if (distance < _min_distance) {
          _min_distance = distance;
          cluster_index = j;
        }
      }
      const sum = cluster_sum[cluster_index];
      sum[0] += data[i++];
      sum[1] += data[i++];
      sum[2] += data[i++];
      sum[3]++;
      if (PIXEL_LEN === 4) i++;
    }
    let allStabled = true;
    for (let i = 0; i < k; i++) {
      const rgbc = cluster_sum[i];
      const count = rgbc[3];
      if (count == 0) {
        const start = Math.floor(Math.random() * (pixelCount - 1)) * PIXEL_LEN;
        new_cluster_centers[i] = /* Array.from */
        data.slice(start, start + 3);
        allStabled = false;
      } else {
        let new_center = new_cluster_centers[i];
        let diffSquared = 0;
        for (let j = 0; j < 3; j++) {
          new_center[j] = rgbc[j] / count;
          diffSquared += (new_center[j] - cluster_centers[i][j]) ** 2;
        }
        if (diffSquared > thresold_squared) {
          allStabled = false;
        }
      }
    }
    if (allStabled) {
      return {
        centroid: new_cluster_centers,
        iteration,
        fit: true,
        label: cluster_sum.map((v) => v[3]),
        size: pixelCount
      };
    }
    const medium = cluster_centers;
    cluster_centers = new_cluster_centers;
    new_cluster_centers = medium;
    iteration++;
    for (let i = 0; i < k; i++) {
      cluster_sum[i].fill(0);
    }
  }
  return {
    centroid: cluster_centers,
    iteration,
    fit: false,
    label: cluster_sum.map((v) => v[3]),
    size: pixelCount
  };
}

/**
 * NeuQuant Neural-Network Quantization Algorithm
 *
 * Copyright (c) 1994 Anthony Dekker
 *
 * See "Kohonen neural networks for optimal colour quantization" in "Network:
 * Computation in Neural Systems" Vol. 5 (1994) pp 351-367. for a discussion of
 * the algorithm.
 *
 * See also http://members.ozemail.com.au/~dekker/NEUQUANT.HTML
 *
 * Any party obtaining a copy of these files from the author, directly or
 * indirectly, is granted, free of charge, a full and unrestricted irrevocable,
 * world-wide, paid up, royalty-free, nonexclusive right and license to deal in
 * this software and documentation files (the "Software"), including without
 * limitation the rights to use, copy, modify, merge, publish, distribute,
 * sublicense, and/or sell copies of the Software, and to permit persons who
 * receive copies from any such party to do so, with the only requirement being
 * that this copyright notice remain intact.
 *
 * Copyright (c) 2012 Johan Nordberg (JavaScript port)
 * Copyright (c) 2014 Devon Govett (JavaScript port)
 */

const prime1 = 499;
const prime2 = 491;
const prime3 = 487;
const prime4 = 503;

const maxprime = Math.max(prime1, prime2, prime3, prime4);
const minpicturebytes = (3 * maxprime);

const defaults = {
  ncycles: 100,
  netsize: 256,
  samplefac: 10
};

const assign = function (target) {
  for (let i = 1, l = arguments.length; i < l; i++) {
    const nextSource = arguments[i];
    if (nextSource != null) {
      for (const nextKey in nextSource) {
        if (Object.prototype.hasOwnProperty.call(nextSource, nextKey)) {
          target[nextKey] = nextSource[nextKey];
        }
      }
    }
  }
  return target
};

class NeuQuant {
  constructor (pixels, options) {
    assign(this, defaults, {pixels}, options);

    if (this.netsize < 4 || this.netsize > 256) {
      throw new Error('Color count must be between 4 and 256')
    }

    if (this.samplefac < 1 || this.samplefac > 30) {
      throw new Error('Sampling factor must be between 1 and 30')
    }

    this.maxnetpos = this.netsize - 1;

    this.netbiasshift = 4;
    this.intbiasshift = 16;
    this.intbias = (1 << this.intbiasshift);
    this.gammashift = 10;
    this.gamma = (1 << this.gammashift);
    this.betashift = 10;
    this.beta = (this.intbias >> this.betashift);
    this.betagamma = (this.beta * this.gamma);

    this.initrad = (this.netsize >> 3);
    this.radiusbiasshift = 6;
    this.radiusbias = (1 << this.radiusbiasshift);
    this.initradius = (this.initrad * this.radiusbias);
    this.radiusdec = 30;

    this.alphabiasshift = 10;
    this.initalpha = (1 << this.alphabiasshift);

    this.radbiasshift = 8;
    this.radbias = (1 << this.radbiasshift);
    this.alpharadbshift = (this.alphabiasshift + this.radbiasshift);
    this.alpharadbias = (1 << this.alpharadbshift);

    this.network = [];
    this.netindex = new Uint32Array(256);
    this.bias = new Uint32Array(this.netsize);
    this.freq = new Uint32Array(this.netsize);
    this.radpower = new Uint32Array(this.netsize >> 3);

    for (let i = 0, l = this.netsize; i < l; i++) {
      let v = (i << (this.netbiasshift + 8)) / this.netsize;
      this.network[i] = new Float64Array([v, v, v, 0]);
      this.freq[i] = this.intbias / this.netsize;
      this.bias[i] = 0;
    }
  }

  unbiasnet () {
    for (let i = 0, l = this.netsize; i < l; i++) {
      this.network[i][0] >>= this.netbiasshift;
      this.network[i][1] >>= this.netbiasshift;
      this.network[i][2] >>= this.netbiasshift;
      this.network[i][3] = i;
    }
  }

  altersingle (alpha, i, b, g, r) {
    this.network[i][0] -= (alpha * (this.network[i][0] - b)) / this.initalpha;
    this.network[i][1] -= (alpha * (this.network[i][1] - g)) / this.initalpha;
    this.network[i][2] -= (alpha * (this.network[i][2] - r)) / this.initalpha;
  }

  alterneigh (radius, i, b, g, r) {
    const lo = Math.abs(i - radius);
    const hi = Math.min(i + radius, this.netsize);

    let j = i + 1;
    let k = i - 1;
    let m = 1;

    while ((j < hi) || (k > lo)) {
      const a = this.radpower[m++];

      if (j < hi) {
        const p = this.network[j++];
        p[0] -= (a * (p[0] - b)) / this.alpharadbias;
        p[1] -= (a * (p[1] - g)) / this.alpharadbias;
        p[2] -= (a * (p[2] - r)) / this.alpharadbias;
      }

      if (k > lo) {
        const p = this.network[k--];
        p[0] -= (a * (p[0] - b)) / this.alpharadbias;
        p[1] -= (a * (p[1] - g)) / this.alpharadbias;
        p[2] -= (a * (p[2] - r)) / this.alpharadbias;
      }
    }
  }

  contest (b, g, r) {
    let bestd = ~(1 << 31);
    let bestbiasd = bestd;
    let bestpos = -1;
    let bestbiaspos = bestpos;

    for (let i = 0, l = this.netsize; i < l; i++) {
      let n = this.network[i];

      let dist = Math.abs(n[0] - b) + Math.abs(n[1] - g) + Math.abs(n[2] - r);
      if (dist < bestd) {
        bestd = dist;
        bestpos = i;
      }

      let biasdist = dist - ((this.bias[i]) >> (this.intbiasshift - this.netbiasshift));
      if (biasdist < bestbiasd) {
        bestbiasd = biasdist;
        bestbiaspos = i;
      }

      let betafreq = (this.freq[i] >> this.betashift);
      this.freq[i] -= betafreq;
      this.bias[i] += (betafreq << this.gammashift);
    }

    this.freq[bestpos] += this.beta;
    this.bias[bestpos] -= this.betagamma;

    return bestbiaspos
  }

  inxbuild () {
    let previouscol = 0;
    let startpos = 0;

    for (let i = 0, l = this.netsize; i < l; i++) {
      let p = this.network[i];
      let q = null;
      let smallpos = i;
      let smallval = p[1];

      for (let j = i + 1; j < l; j++) {
        q = this.network[j];
        if (q[1] < smallval) {
          smallpos = j;
          smallval = q[1];
        }
      }
      q = this.network[smallpos];

      if (i !== smallpos) {
        [p[0], q[0]] = [q[0], p[0]];
        [p[1], q[1]] = [q[1], p[1]];
        [p[2], q[2]] = [q[2], p[2]];
        [p[3], q[3]] = [q[3], p[3]];
      }

      if (smallval !== previouscol) {
        this.netindex[previouscol] = (startpos + i) >> 1;
        for (let j = previouscol + 1; j < smallval; j++) {
          this.netindex[j] = i;
        }
        previouscol = smallval;
        startpos = i;
      }
    }

    this.netindex[previouscol] = (startpos + this.maxnetpos) >> 1;
    for (let i = previouscol + 1; i < 256; i++) {
      this.netindex[i] = this.maxnetpos;
    }
  }

  learn () {
    const lengthcount = this.pixels.length;
    const alphadec = 30 + ((this.samplefac - 1) / 3);
    const samplepixels = lengthcount / (3 * this.samplefac);

    let delta = samplepixels / this.ncycles | 0;
    let alpha = this.initalpha;
    let radius = this.initradius;

    let rad = radius >> this.radiusbiasshift;

    if (rad <= 1) {
      rad = 0;
    }

    for (let i = 0; i < rad; i++) {
      this.radpower[i] = alpha * (((rad * rad - i * i) * this.radbias) / (rad * rad));
    }

    let step;
    if (lengthcount < minpicturebytes) {
      this.samplefac = 1;
      step = 3;
    } else if ((lengthcount % prime1) !== 0) {
      step = 3 * prime1;
    } else if ((lengthcount % prime2) !== 0) {
      step = 3 * prime2;
    } else if ((lengthcount % prime3) !== 0) {
      step = 3 * prime3;
    } else {
      step = 3 * prime4;
    }

    let pix = 0;

    for (let i = 0; i < samplepixels;) {
      let b = (this.pixels[pix] & 0xff) << this.netbiasshift;
      let g = (this.pixels[pix + 1] & 0xff) << this.netbiasshift;
      let r = (this.pixels[pix + 2] & 0xff) << this.netbiasshift;

      let j = this.contest(b, g, r);
      this.altersingle(alpha, j, b, g, r);
      if (rad !== 0) {
        this.alterneigh(rad, j, b, g, r);
      }

      pix += step;
      if (pix >= lengthcount) {
        pix -= lengthcount;
      }

      if (delta === 0) {
        delta = 1;
      }

      if (++i % delta === 0) {
        alpha -= alpha / alphadec;
        radius -= radius / this.radiusdec;
        rad = radius >> this.radiusbiasshift;

        if (rad <= 1) {
          rad = 0;
        }

        for (let k = 0; k < rad; k++) {
          this.radpower[k] = alpha * (((rad * rad - k * k) * this.radbias) / (rad * rad));
        }
      }
    }
  }

  buildColorMap () {
    this.learn();
    this.unbiasnet();
    this.inxbuild();
  }

  getColorMap () {
    const map = new Uint8Array(this.netsize * 3);
    const index = new Uint8Array(this.netsize);

    for (let i = 0, l = this.netsize; i < l; i++) {
      index[this.network[i][3]] = i;
    }

    for (let i = 0, j = 0, k = 0, l = this.netsize; i < l; i++) {
      k = index[i];
      map[j++] = this.network[k][0] & 0xff;
      map[j++] = this.network[k][1] & 0xff;
      map[j++] = this.network[k][2] & 0xff;
    }

    return map
  }
}

function findClosest (palette, r, g, b) {
  let minpos = 0;
  let mind = 256 * 256 * 256;

  for (let i = 0, l = palette.length; i < l;) {
    const dr = r - palette[i++];
    const dg = g - palette[i++];
    const db = b - palette[i];
    const d = dr * dr + dg * dg + db * db;
    const pos = i / 3 | 0;

    if (d < mind) {
      mind = d;
      minpos = pos;
    }

    i++;
  }

  return minpos
}

function palette (pixels, options) {
  const nq = new NeuQuant(pixels, options);
  nq.buildColorMap();
  return nq.getColorMap()
}

function indexed (pixels, palette) {
  const indexed = new Uint8Array(pixels.length / 3);
  const memo = {};

  for (let i = 0, j = 0, l = pixels.length; i < l;) {
    const r = pixels[i++];
    const g = pixels[i++];
    const b = pixels[i++];
    const k = r << 16 | g << 8 | b;

    if (k in memo) {
      indexed[j++] = memo[k];
    } else {
      indexed[j++] = memo[k] = findClosest(palette, r, g, b);
    }
  }

  return indexed
}

function rgba2Rgb(data) {
  const rgb = new Uint8ClampedArray(data.length / 4 * 3);
  const len = data.length;
  let rgb_i = 0;
  for (let i = 0; i < len; ) {
    for (let j = 0; j < 3; j++) {
      rgb[rgb_i++] = data[i++];
    }
    i++;
  }
  return rgb;
}
function neuquant(img, k, samplefac = 1) {
  const img_rgb = rgba2Rgb(img);
  const palette$1 = palette(img_rgb, { netsize: k, samplefac });
  const indexed_pixel = indexed(img_rgb, palette$1);
  const pixel = [];
  const len = palette$1.length;
  for (let i = 0; i < len; ) {
    pixel.push([palette$1[i++], palette$1[i++], palette$1[i++], 255]);
  }
  return { centroid: pixel, label: count(k, indexed_pixel) };
}
function count(k, index) {
  const counts = new Array(k).fill(0);
  for (const i of index) {
    counts[i]++;
  }
  return counts;
}

function _prepare2DContext(width, height) {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  canvas.height = height;
  canvas.width = width;
  return ctx;
}
function _prepare2DContextAsync(width, height) {
  const canvas = new OffscreenCanvas(width, height);
  const ctx = canvas.getContext("2d");
  return ctx;
}
function awaitImage(imgElement) {
  return new Promise((resolve, reject) => {
    if (imgElement.complete) {
      resolve();
    } else {
      imgElement.addEventListener("load", (e) => {
        resolve(e);
      });
      imgElement.addEventListener("error", (reason) => {
        reject(reason);
      });
    }
  });
}
function _readImage(prepareCtx, imgSource) {
  const { naturalWidth, naturalHeight } = imgSource;
  const ctx = prepareCtx(naturalWidth, naturalHeight);
  ctx.drawImage(imgSource, 0, 0, naturalWidth, naturalHeight);
  return ctx.getImageData(0, 0, naturalWidth, naturalHeight);
}
const readImage = _readImage.bind(void 0, _prepare2DContext);
const readImageOffscreen = _readImage.bind(void 0, _prepare2DContextAsync);
function _readImageDownsampling(prepareCtx, imgSource, maxSample) {
  let { naturalWidth: width, naturalHeight: height } = imgSource;
  const scale = width * height / maxSample;
  if (scale > 1) {
    const scaleSqrt = Math.sqrt(scale);
    width = width / scaleSqrt | 0;
    height = height / scaleSqrt | 0;
  }
  const ctx = prepareCtx(width, height);
  ctx.drawImage(imgSource, 0, 0, width, height);
  return ctx.getImageData(0, 0, width, height);
}
const readImageDownsampling = _readImageDownsampling.bind(void 0, _prepare2DContext);
const readImageDownsamplingOffscreen = _readImageDownsampling.bind(void 0, _prepare2DContextAsync);

function convertToLab(source) {
  const array = [];
  for (let i = 0; i < source.length; i += 4) {
    const pixel = source.slice(i, i + 4);
    const alpha = pixel[3];
    const pixel_rgb = pixel.slice(0, 3);
    const pixel_xyz = rgb.xyz(pixel_rgb);
    const pixel_lab = xyz.lab(pixel_xyz);
    array.push(...pixel_lab, alpha);
  }
  return array;
}
function labaToRGBA(laba) {
  const pixel_lab = laba.slice(0, 3);
  const alpha = laba[3];
  const pixel_xyz = lab.xyz(pixel_lab);
  const pixel_rgb = xyz.rgb(pixel_xyz);
  pixel_rgb.push(alpha);
  return pixel_rgb;
}

function max_min_of_three([a, b, c]) {
  if (a > b) {
    if (b > c) {
      return [a, c];
    } else {
      if (a > c) {
        return [a, b];
      } else {
        return [c, b];
      }
    }
  } else {
    if (b > c) {
      if (c > a) {
        return [b, a];
      } else {
        return [b, c];
      }
    } else {
      return [c, a];
    }
  }
}

function normalizeRGBA(rgba) {
  return rgba.map((v) => v / 255);
}
function rgbaToHSLA(rgba) {
  let h, s;
  const [max, min] = max_min_of_three(rgba);
  const diff = max - min;
  const light_2x = max + min;
  if (diff == 0) {
    h = 0;
    s = 0;
  } else {
    if (max == rgba[0]) {
      const temp_h = 60 * (rgba[1] - rgba[2]) / diff;
      if (temp_h < 0) {
        h = temp_h + 360;
      } else {
        h = temp_h;
      }
    } else if (max == rgba[1]) {
      h = 60 * (rgba[2] - rgba[0]) / diff + 120;
    } else {
      h = 60 * (rgba[0] - rgba[1]) / diff + 240;
    }
    if (light_2x == 0) {
      s = 0;
    } else if (light_2x <= 1) {
      s = diff / light_2x;
    } else {
      s = diff / (2 - light_2x);
    }
  }
  return [h, s, light_2x / 2, rgba[3]];
}
const getVector4Comparer = (sort = [0, 1, 2, 3]) => (a, b) => {
  let result;
  for (const s of sort) {
    result = a[s] - b[s];
    if (result != 0) {
      return result;
    }
  }
  return result;
};
const hslaCSSText = ([h, s, l, a]) => `hsla(${h}deg,${s * 100}%,${l * 100}%,${a})`;
const rgbaCSSText = (pixel) => `rgba(${pixel.map((v) => Math.floor(v)).join(",")})`;

export { awaitImage, convertToLab, getVector4Comparer, hslaCSSText, kmeans, labaToRGBA, neuquant, normalizeRGBA, readImage, readImageDownsampling, readImageDownsamplingOffscreen, readImageOffscreen, rgbaCSSText, rgbaToHSLA };
